/**
 * Kuratierter Katalog der pro Tenant überschreibbaren UI-Texte.
 *
 * Single Source of Truth für drei Konsumenten:
 *  - Admin-Oberfläche (rendert je Eintrag ein DE-/EN-Feld mit `label` und zeigt
 *    `defaults` als Platzhalter/Hinweis, welcher Text aktuell angezeigt wird),
 *  - Write-Validator (`validateTenantConfigInput` erlaubt nur diese Keys),
 *  - Patienten-Frontend (legt die Werte per `i18n.addResource(<locale>, ns,
 *    <key>, <text>)` über die eingebauten Standardtexte).
 *
 * `key` ist der i18next-Pfad im Default-Namespace, zugleich Speicher-Key im
 * Override-Objekt. `defaults` spiegelt die eingebauten Standardtexte aus
 * `frontend/src/i18n-module/{de,en}.json`; ein Frontend-Test sichert, dass
 * beide nicht auseinanderlaufen. Leere/fehlende Overrides fallen auf diesen
 * Standardtext zurück. Bewusst auf Meldungen beschränkt (Erfolg, fachliche und
 * technische Fehler) — Bedien-Texte bleiben zentral.
 */
export interface TenantTextEntry {
  /** i18next-Key im Default-Namespace, zugleich Speicher-Key im Override-Objekt. */
  readonly key: string;
  /** Menschliche Beschriftung für die Support-Oberfläche. */
  readonly label: string;
  /** Mehrzeiliges Eingabefeld (lange Texte) vs. einzeilig (Titel). */
  readonly multiline: boolean;
  /** Eingebaute Standardtexte je Sprache (Platzhalter/Hinweis im Admin). */
  readonly defaults: { readonly de: string; readonly en: string };
}

const PHONE_HINT = ' (Platzhalter {{phoneNumber}} verfügbar)';

export const TENANT_TEXT_CATALOG: readonly TenantTextEntry[] = [
  {
    key: 'success.message',
    label: 'Erfolgsmeldung (Absage eingegangen)',
    multiline: true,
    defaults: {
      de: 'Vielen Dank, Ihre Terminabsage ist bei uns eingegangen.',
      en: 'Thank you, we have received your cancellation.',
    },
  },
  {
    key: 'businessError.reasons.TOKEN_EXPIRED.title',
    label: 'Fehler „Link abgelaufen“ — Titel',
    multiline: false,
    defaults: { de: 'Link abgelaufen', en: 'Link expired' },
  },
  {
    key: 'businessError.reasons.TOKEN_EXPIRED.body',
    label: 'Fehler „Link abgelaufen“ — Text',
    multiline: true,
    defaults: {
      de: 'Der Absage-Link ist abgelaufen. Bitte rufen Sie uns an, damit wir Ihren Termin gemeinsam absagen können.',
      en: 'This cancellation link has expired. Please call the practice so we can cancel your appointment together.',
    },
  },
  {
    key: 'businessError.reasons.TOKEN_ALREADY_USED.title',
    label: 'Fehler „Link bereits verwendet“ — Titel',
    multiline: false,
    defaults: { de: 'Link bereits verwendet', en: 'Link already used' },
  },
  {
    key: 'businessError.reasons.TOKEN_ALREADY_USED.body',
    label: 'Fehler „Link bereits verwendet“ — Text',
    multiline: true,
    defaults: {
      de: 'Dieser Absage-Link wurde bereits verwendet. Falls Sie unsicher sind, ob Ihr Termin abgesagt ist, rufen Sie uns bitte kurz an.',
      en: 'This cancellation link has already been used. If you are unsure whether your appointment is cancelled, please give us a quick call.',
    },
  },
  {
    key: 'businessError.reasons.PRACTICE_REJECTED.title',
    label: 'Fehler „Angaben stimmen nicht überein“ — Titel',
    multiline: false,
    defaults: { de: 'Angaben stimmen nicht überein', en: 'Details do not match' },
  },
  {
    // N-8: neutral formuliert — nennt das Geburtsdatum als Prüf-Hinweis, ohne
    // zu verraten, ob die Eingabe falsch war oder kein Datensatz existiert
    // (Enumeration-Schutz bleibt erhalten; der Reason-Code ist unverändert).
    key: 'businessError.reasons.PRACTICE_REJECTED.body',
    label: 'Fehler „Angaben stimmen nicht überein“ — Text',
    multiline: true,
    defaults: {
      de: 'Die Angaben stimmen nicht überein. Bitte prüfen Sie Ihr Geburtsdatum. Falls es korrekt ist, rufen Sie uns bitte kurz an.',
      en: 'The details do not match. Please check your date of birth. If it is correct, please give us a quick call.',
    },
  },
  {
    key: 'businessError.reasons.TOO_LATE.title',
    label: 'Fehler „Nur noch telefonisch“ — Titel',
    multiline: false,
    defaults: { de: 'Absage nur noch telefonisch', en: 'Cancellation by phone only' },
  },
  {
    key: 'businessError.reasons.TOO_LATE.body',
    label: 'Fehler „Nur noch telefonisch“ — Text',
    multiline: true,
    defaults: {
      de: 'Eine Online-Absage ist für diesen Termin nicht mehr möglich. Bitte rufen Sie uns kurz an, damit wir Ihre Absage entgegennehmen können.',
      en: 'Online cancellation is no longer possible for this appointment. Please call the practice so we can take your cancellation.',
    },
  },
  {
    key: 'technicalError.generic.message',
    label: `Technischer Fehler — Text${PHONE_HINT}`,
    multiline: true,
    defaults: {
      de: 'Technischer Fehler. Bitte versuchen Sie es in wenigen Minuten erneut oder rufen Sie uns an: {{phoneNumber}}.',
      en: 'Technical error. Please try again in a few minutes or call us: {{phoneNumber}}.',
    },
  },
  {
    key: 'technicalError.pinningFailure.message',
    label: `Technischer Fehler „Sicherheit“ — Text${PHONE_HINT}`,
    multiline: true,
    defaults: {
      de: 'Der Link konnte nicht sicher geöffnet werden. Bitte tippen Sie den Link direkt aus der Original-SMS an — nicht aus weitergeleiteten Nachrichten oder kopierten Texten. Falls das nicht hilft, rufen Sie uns an: {{phoneNumber}}.',
      en: 'The link could not be opened securely. Please tap the link directly from the original SMS — not from forwarded messages or copied text. If that does not help, call us: {{phoneNumber}}.',
    },
  },
];

/** Schnelles Lookup, welche Keys überschreibbar sind (Validator-Allowlist). */
export const TENANT_TEXT_KEYS: ReadonlySet<string> = new Set(
  TENANT_TEXT_CATALOG.map((entry) => entry.key),
);

/** Sprachen, für die Overrides gepflegt werden können. */
export const TENANT_TEXT_LOCALES = ['de', 'en'] as const;
export type TenantTextLocale = (typeof TENANT_TEXT_LOCALES)[number];

/** Override-Struktur: pro Sprache ein `key → Text`-Mapping. */
export type TenantTextOverrides = {
  readonly [Locale in TenantTextLocale]?: Readonly<Record<string, string>>;
};
